// Fetches paginated Store Action lists and adapts the API response to the feature domain model.
import {
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  getPrintEditPricetagGrid,
  getStoreActionsItem,
} from '../../../api/user';
import {
  hasSuccessfulStoreActionStatus,
  normalizeStoreActionStatus,
  StoreAction,
  StoreActionPage,
  StoreActionPageRequest,
  StoreActionTransportResponse,
  StoreActionWireStatus,
  STORE_ACTION_PAGE_SIZE,
  STORE_ACTION_WIRE_STATUS,
} from './storeActionDomain';

const STORE_ACTION_QUERY_KEY = 'store-actions';

export interface ExpandedPagesComposition {
  readonly items: ReadonlyArray<StoreAction>;
  readonly pages: ReadonlyArray<StoreActionPage>;
  readonly total: number;
}

export const composeExpandedPages = (
  pages: ReadonlyArray<StoreActionPage>
): ExpandedPagesComposition => {
  const actionIds = new Set<string | number | null>();
  const items: StoreAction[] = [];
  for (const page of pages) {
    page.items.forEach((item) => {
      if (!actionIds.has(item.storeActionItemId)) {
        actionIds.add(item.storeActionItemId);
        items.push(item);
      }
    });
  }
  return { items, pages, total: pages.length > 0 ? pages[0].total : 0 };
};

export const storeActionQueryKeys = {
  list: (storeNumber: string, status: StoreActionWireStatus) =>
    [STORE_ACTION_QUERY_KEY, storeNumber, status] as const,
  priceTagQueue: (storeNumber: string) =>
    [STORE_ACTION_QUERY_KEY, 'price-tag-queue', storeNumber] as const,
};

type PriceTagResponse = {
  readonly status: number;
  readonly data: {
    readonly response: ReadonlyArray<unknown>;
  };
};

const toStoreActionPage = (
  response: StoreActionTransportResponse,
  request: StoreActionPageRequest,
  role: string
): StoreActionPage => {
  // CAR users must not see DSS actions, so apply the same rule to rows and category totals.
  const items = response.data.storeActionDetails
    .filter((item) => role !== 'CAR' || item.storeActionType !== 'DSS')
    .map<StoreAction>((item) => ({
      active: item.active,
      createdAt: item.createdDate,
      description: item.description,
      redirectUrl: item.redirectUrl,
      storeActionCategory: item.storeActionCategory,
      status: normalizeStoreActionStatus(item.active),
      storeId: item.storeId,
      customerId: item.customerId,
      agreementId: item.agreementId,
      inventoryId: item.inventoryId,
      externalIdentifier: item.externalIdentifier,
      storeActionItemId: item.storeActionItemId,
      storeActionType: item.storeActionType,
    }));
  const countsByCategory = response.data.counts.reduce<Record<string, number>>(
    (counts, entry) =>
      role === 'CAR' && entry.category === 'DSS'
        ? counts
        : { ...counts, [entry.category]: entry.count },
    {}
  );
  const nextOffset =
    request.offset * request.limit < response.data.totalCount
      ? request.offset + 1
      : null;

  return {
    countsByCategory,
    items,
    limit: request.limit,
    nextOffset,
    offset: request.offset,
    total:
      role === 'CAR'
        ? Math.max(
            0,
            response.data.totalCount -
              (response.data.counts.find((entry) => entry.category === 'DSS')
                ?.count || 0)
          )
        : response.data.totalCount,
  };
};

const fetchStoreActionPage = async (
  request: StoreActionPageRequest,
  signal?: AbortSignal
): Promise<StoreActionTransportResponse> => {
  const response = await getStoreActionsItem(request, { signal });

  if (response.status !== 200) {
    throw new Error('Invalid store-action response');
  }
  return response;
};

type StoreActionListQueryOptions = {
  readonly enabled: boolean;
  readonly role: string;
  readonly status: StoreActionWireStatus;
  readonly storeNumber: string;
};

const useStoreActionListQuery = ({
  enabled,
  role,
  status,
  storeNumber,
}: StoreActionListQueryOptions): UseInfiniteQueryResult<
  StoreActionPage,
  Error
> =>
  useInfiniteQuery<StoreActionPage, Error>({
    enabled,
    queryKey: storeActionQueryKeys.list(storeNumber, status),
    queryFn: async ({ signal, pageParam = 1 }) => {
      const offset = typeof pageParam === 'number' ? pageParam : 1;
      const request: StoreActionPageRequest = {
        limit: STORE_ACTION_PAGE_SIZE,
        offset,
        status,
        storeNumber,
      };
      const response = await fetchStoreActionPage(request, signal);

      return toStoreActionPage(response, request, role);
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    useErrorBoundary: true,
  });

export const useStoreActionActiveListQuery = (
  storeNumber: string,
  enabled: boolean,
  role = ''
): UseInfiniteQueryResult<StoreActionPage, Error> =>
  useStoreActionListQuery({
    enabled,
    role,
    status: STORE_ACTION_WIRE_STATUS.active,
    storeNumber,
  });

export const useStoreActionCompletedListQuery = (
  storeNumber: string,
  enabled: boolean,
  role = ''
): UseInfiniteQueryResult<StoreActionPage, Error> =>
  useStoreActionListQuery({
    enabled,
    role,
    status: STORE_ACTION_WIRE_STATUS.completed,
    storeNumber,
  });

export const useStoreActionPriceTagQueueQuery = (storeNumber: string) =>
  useQuery<boolean, Error>({
    queryKey: storeActionQueryKeys.priceTagQueue(storeNumber),
    queryFn: async ({ signal }) => {
      const response: unknown = await getPrintEditPricetagGrid(storeNumber, {
        signal,
      });
      if (!hasSuccessfulStoreActionStatus(response)) {
        throw new Error('Invalid price-tag response');
      }
      return (response as PriceTagResponse).data.response.length > 0;
    },
    useErrorBoundary: true,
  });
